<script lang="ts" setup>
import { computed } from 'vue';
import type { AirportCounters } from '../model/types';

const isOpen = defineModel<boolean>({ required: true });
const selectedCounterId = defineModel<string>('selectedCounterId', { required: true });

const props = defineProps<{
    isClosingCompleted: boolean;
    isPastClosingTime: boolean;
    isLoading: boolean;
    airportCounters: AirportCounters;
}>();

const emit = defineEmits<{
    execute: [];
    revert: [];
    counterChange: [];
}>();

const counterOptions = computed(() =>
    props.airportCounters.flatMap((airport) => [
        {
            title: airport.airport.japanese_name,
            value: `header-${airport.airport.uuid}`,
            props: { disabled: true },
        },
        ...airport.airport_receptions.map((reception) => ({
            title: `  ${reception.japanese_name}`,
            value: reception.uuid,
        })),
    ])
);

const toReceptionWithAirportName = (airport: AirportCounters[number]) =>
    airport.airport_receptions.map((reception) => ({
        airportName: airport.airport.japanese_name,
        reception,
    }));

const selectedCounterName = computed(() => {
    const found = props.airportCounters
        .flatMap((counter) => toReceptionWithAirportName(counter))
        .find(({ reception }) => reception.uuid === selectedCounterId.value);

    return found ? `${found.airportName} - ${found.reception.japanese_name}` : '';
});

const statusText = computed(() => (props.isClosingCompleted ? '締め処理済み' : '未実行'));

const handleCounterChange = () => {
    emit('counterChange');
};

const handleExecute = () => {
    emit('execute');
};

const handleRevert = () => {
    emit('revert');
};

const handleClose = () => {
    isOpen.value = false;
};
</script>

<template>
    <v-dialog v-model="isOpen" max-width="500" persistent>
        <v-card>
            <v-card-title class="text-h6 pa-4"> 締め処理 </v-card-title>

            <v-divider />

            <v-card-text class="pa-4">
                <!-- TODO: 空港スタッフの場合は非表示にする（AT管理スタッフのみ表示）。authStoreにユーザー情報追加後に対応 -->
                <div class="mb-4">
                    <label class="text-body-2 font-weight-bold d-block mb-2">対象カウンター</label>
                    <v-select
                        v-model="selectedCounterId"
                        :items="counterOptions"
                        item-title="title"
                        item-value="value"
                        item-props="props"
                        density="compact"
                        variant="outlined"
                        hide-details
                        @update:model-value="handleCounterChange"
                    />
                    <p v-if="selectedCounterName" class="text-body-2 text-grey mt-1">
                        {{ selectedCounterName }}
                    </p>
                </div>

                <v-list>
                    <v-list-item>
                        <v-list-item-title class="font-weight-bold">現在の状態</v-list-item-title>
                        <v-list-item-subtitle>{{ statusText }}</v-list-item-subtitle>
                    </v-list-item>

                    <v-list-item v-if="!isClosingCompleted">
                        <v-list-item-title class="font-weight-bold">操作</v-list-item-title>
                        <v-list-item-subtitle>締め処理を実行</v-list-item-subtitle>
                    </v-list-item>

                    <v-list-item v-if="!isClosingCompleted">
                        <v-list-item-title class="font-weight-bold">影響</v-list-item-title>
                        <v-list-item-subtitle>配送予定時間が翌日扱いになります</v-list-item-subtitle>
                    </v-list-item>
                </v-list>
            </v-card-text>

            <v-divider />

            <p v-if="!isClosingCompleted && !isPastClosingTime" class="text-body-2 text-center text-error pa-4 pb-0">
                締め時間以降でないと締め処理ができません。
            </p>

            <v-card-actions class="pa-4 ga-3">
                <v-btn variant="outlined" color="grey" class="modal-btn" @click="handleClose"> キャンセル </v-btn>
                <v-spacer />
                <v-btn
                    v-if="!isClosingCompleted"
                    color="primary"
                    variant="flat"
                    class="modal-btn"
                    :disabled="!isPastClosingTime || isLoading"
                    :loading="isLoading"
                    @click="handleExecute"
                >
                    締め処理を実行
                </v-btn>
                <v-btn
                    v-else
                    color="warning"
                    variant="flat"
                    class="modal-btn"
                    :disabled="isLoading"
                    :loading="isLoading"
                    @click="handleRevert"
                >
                    締め処理を復旧
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>
.modal-btn {
    min-height: 44px;
    padding: 0 20px;
}
</style>
